import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { buttonStyles } from '../../utils/colors';
import ViewTabs from '../../components/crm/ViewTabs';
import ViewToolbar from '../../components/crm/ViewToolbar';
import ViewEditorModal from '../../components/crm/ViewEditorModal';
import CreateRecordModal from '../../components/crm/CreateRecordModal';
import Pagination from '../../components/crm/Pagination';
import KanbanBoard from '../../components/crm/KanbanBoard';
import useObjectView from '../../hooks/useObjectView';
import { useDataObject } from '../DataObjectSlugResolver';

function FastestFormCRMObjectList() {
  const dataObject = useDataObject();
  const objectDefinitionId = dataObject?.id;
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const {
    views, activeView, activeViewId, entries, tableColumns, loading,
    query, setQuery, currentPage, setCurrentPage, pageSize, totalItems,
    sortBy, sortOrder,
    showFilterModal, setShowFilterModal, filterMode, editingView,
    availableFields, fieldDefMap, fetchEntries,
    handleSort, handleSelectView, handleCreateView, handleEditView, handleSaveView, handleDeleteView,
    handleInlineColumnsChange, handleInlineFiltersChange, handleDuplicateView,
    handleReorderViews, handleTogglePin,
    isKanban, kanbanColumns, kanbanGroupedEntries, kanbanDisplayFields, handleMoveRecord,
  } = useObjectView(objectDefinitionId);

  const objectDef = activeView?.object_definition;
  const objectName = objectDef?.name || 'Records';

  const handleRowClick = (entry) => {
    const slug = dataObject?.slug_plural || dataObject?.slug;
    navigate(`/data/${slug}/${entry.id}`);
  };

  const handleRecordCreated = () => {
    fetchEntries();
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div>
      <ViewTabs
        views={views}
        activeViewId={activeViewId}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onEditView={handleEditView}
        onDeleteView={handleDeleteView}
        onReorderViews={handleReorderViews}
        onTogglePin={handleTogglePin}
      />

      <div className="flex items-center justify-between mb-4">
        <ViewToolbar
          query={query}
          setQuery={setQuery}
          objectName={objectName}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          activeView={activeView}
          availableFields={availableFields}
          isKanban={isKanban}
          onColumnsChange={handleInlineColumnsChange}
          onFiltersChange={handleInlineFiltersChange}
          onEditView={() => handleEditView(activeView)}
          onDuplicateView={handleDuplicateView}
          onDeleteView={() => activeView && handleDeleteView(activeView.id)}
        />
        <button
          onClick={() => setShowCreateModal(true)}
          className={`${buttonStyles.primary} flex items-center gap-2 ml-4 flex-shrink-0`}
        >
          <Plus className="w-4 h-4" />
          New {objectDef?.name || 'Record'}
        </button>
      </div>

      {isKanban ? (
        <KanbanBoard
          columns={kanbanColumns}
          groupedEntries={kanbanGroupedEntries}
          onMoveRecord={handleMoveRecord}
          onRecordClick={handleRowClick}
          displayFields={kanbanDisplayFields}
          fieldDefMap={fieldDefMap}
        />
      ) : (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
          {entries.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">No {objectName.toLowerCase()} yet</h3>
              <p className="mt-2 text-gray-500 dark:text-gray-400">Create your first record to get started.</p>
            </div>
          ) : (
            <div>
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg dark:bg-gray-700 dark:border-gray-600">
                <div className="flex gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
                  {tableColumns.map((col) => (
                    <div key={col.key} className="flex-1 min-w-0">{col.label}</div>
                  ))}
                </div>
              </div>

              {entries.map((entry) => (
                <div
                  key={entry.id}
                  className="px-6 py-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors dark:hover:bg-gray-700 dark:border-gray-600"
                  onClick={() => handleRowClick(entry)}
                >
                  <div className="flex gap-4 items-center">
                    {tableColumns.map((col) => (
                      <div key={col.key} className="flex-1 min-w-0 text-sm text-gray-700 truncate dark:text-gray-300">
                        {col.render ? col.render(entry[col.key]) : (typeof entry[col.key] === 'object' ? (entry[col.key]?.display_name || '\u2014') : (entry[col.key] || '\u2014'))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={pageSize}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      )}

      <ViewEditorModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onSave={handleSaveView}
        mode={filterMode}
        initialView={editingView || activeView}
        availableFields={availableFields}
      />

      <CreateRecordModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleRecordCreated}
        objectDefinitionId={objectDefinitionId}
        fieldDefinitions={availableFields}
      />
    </div>
  );
}

export default FastestFormCRMObjectList;
